<template>
  <div
    v-if="getUser"
    class="platform-progress"
  >
    <v-expansion-panels
      v-model="panel"
      multiple
      class="elevation-10"
    >
      <v-expansion-panel elevation="10">
        <v-expansion-panel-header>
          <div class="d-flex justify-space-between align-center full-width">
            <div :class="stepsLeft === 0 ? 'mr-8' : '0'">
              {{ $t("globals.platform.title", { name: $ls.get("config").name }) }}
            </div>
            <div
              v-if="stepsLeft > 0"
              class="steps-count"
            >
              {{ stepsLeft }}
            </div>
          </div>

          <template #actions>
            <v-icon>
              mdi-chevron-up
            </v-icon>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <template v-if="stepsLeft !== 0">
            <v-progress-linear
              height="25"
              :value="progress"
              rounded
            >
              <template #default="{ value }">
                <strong>{{ Math.ceil(value) }}%</strong>
              </template>
            </v-progress-linear>
            <v-card class="info-card">
              <v-sheet dense>
                <v-list-item
                  v-for="step in steps"
                  :key="step.title"
                  :disabled="step.done"
                  class="pa-0"
                  @click="$router.push(`${step.path}`)"
                >
                  <v-list-item-icon>
                    <div
                      class="circle-outline"
                      :class="step.done ? 'check-circle' : ''"
                    >
                      <v-icon
                        v-if="step.done"
                        size="30px"
                      >
                        mdi-check
                      </v-icon>
                    </div>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{
                        step.title
                      }}
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="step.subtitle">
                      {{
                        step.subtitle
                      }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action v-if="step.path === '/modules/configuration'">
                    <button
                      class="btn-delete"
                      @click.stop="confirmDeleteStep(step)"
                    >
                      <IconCustom
                        :color="$vuetify.theme.dark ? '#FFFFFF' : '#96A2AE'"
                        name="icn20_trash_can"
                      />
                    </button>
                  </v-list-item-action>
                </v-list-item>
              </v-sheet>
            </v-card>
          </template>
          <v-card
            v-else
            class="success-steps"
          >
            <v-card-text
              class="mt-5 mb-5 text-center"
            >
              <helper-success-animation class="mb-4" />
              {{ $t("globals.platform.configurationComplete") }}
            </v-card-text>
            <v-card-actions>
              <v-btn
                :disabled="loading"
                class="primary btn-close"
                @click="closePlatformProgress"
              >
                {{ $t('globals.closeButton') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <helper-confirm-dialog
      v-if="confirmDialog"
      :show-dialog="confirmDialog"
      :text="$t('globals.platform.stepReallyDelete', {name: selectedStep.title})"
      @closeConfirmDialog="closeConfirmDialog"
    />
  </div>
</template>
<script>
import { mapGetters, mapMutations } from "vuex";
import store from "@/store";

import WL_GATEWAYS from "@/graphql/queries/WhiteLabelGateways.gql";
import WHITELABEL_USERS_QUERY from '@/graphql/queries/WhiteLabelUsers.gql';
import ME_QUERY from "@/graphql/queries/Me.gql";
import UPDATE_USER_MUTATION from "@/graphql/mutations/UpdateUser.gql";
import NODULES_QUERY from "@/graphql/queries/Modules.gql";
import PRICINGS_QUERY from "@/graphql/queries/ProviderPricings.gql";

import IconCustom from "@/components/helper/IconCustom.vue";

export default {
  name: "PlatformProgress",
  components: {
    IconCustom,
  },
  data() {
    return {
      panel: [0],
      progress: 0,
      loading: false,
      confirmDialog: false,
      selectedStep: null,
      stepsLeft: 6,
      steps: {
        step1: {
          title: this.$t("globals.platform.steps.step1"),
          subtitle: "",
          path: "",
          done: true
        },
        step2: {
          title: this.$t("globals.platform.steps.step2"),
          subtitle: this.$t("globals.platform.steps.subtitle2"),
          path: "/tariffs",
          done: false
        },
        step3: {
          title: this.$t("globals.platform.steps.step3"),
          subtitle: this.$t("globals.platform.steps.subtitle3"),
          path: "/whitelabel/create",
          done: false
        },
        step4: {
          title: this.$t("globals.platform.steps.step4"),
          subtitle: this.$t("globals.platform.steps.subtitle4"),
          path: "/gateways",
          done: false
        },
        step5: {
          title: this.$t("globals.platform.steps.step5"),
          subtitle: this.$t("globals.platform.steps.subtitle5"),
          path: "/modules/configuration",
          done: false
        },
        step6: {
          title: this.$t("globals.platform.steps.step6"),
          subtitle: this.$t("globals.platform.steps.subtitle6"),
          path: "/users",
          done: false
        }
      }
    }
  },
  computed: {
    ...mapGetters("user", ["getUser"]),
    ...mapGetters("whitelabel", ["getWhiteLabel"]),
  },
  watch: {
    getWhiteLabel: {
      handler: function (val) {
        if(val) {
          this.steps.step3.done = true;
        }
      },
      deep: true
    },
    getUser: {
      handler: function (val) {
        if(val && val.pricing_purchase && val.pricing_purchase.status === "active") {
          this.steps.step2.done = true;
        }
      },
      deep: true
    },
    whiteLabelGateways: {
      handler: function (val) {
        if(val && val.length !== 0) {
          const activeGateway = val.find(item => item.status === "enabled");
          if(activeGateway) {
            this.steps.step4.done = true;
          }
        }
      },
    },
    whiteLabelUsers: {
      handler: function (val) {
        if(val && val.data.length !== 0) {
          this.steps.step6.done = true;
        }
      },
    },
    whiteLabelModules: {
      handler: function (val) {
        if(val && val.length !== 0 && !localStorage.getItem('stepSRM')) {
          const searchSRM = val.filter(item => item.module === "bitrix24_connector" || item.module === "amocrm_connector" || item.module === "pipedrive_connector");
          if(searchSRM.length !== 0) {
            for(let item of searchSRM ) {
              let configParse = JSON.parse(item.custom_config);
              if(!this._.isEmpty(configParse)) {
                this.steps.step5.done = true;
                return
              }
            }
          }

        }
      },
    },
    steps: {
      handler(val) {
        this.checkedProgress(val);
      },
      deep: true
    }
  },
  mounted() {
    store.subscribe((mutation) => {
      if (mutation.type == "app/setReloadProgress") {
        this.$apollo.queries.whiteLabelModules.refetch();
        this.$apollo.queries.whiteLabelUsers.refetch();
        this.$apollo.queries.whiteLabelGateways.refetch();
        this.setReloadProgress(false);

      }
    });
    if(localStorage.getItem('stepSRM')){
      delete this.steps.step5;
    }
    if(this.getUser && this.getUser.pricing_purchase && this.getUser.pricing_purchase.status === "active") {
      this.steps.step2.done = true;
    }
    if(this.getWhiteLabel) {
      this.steps.step3.done = true;
    }
    this.checkedProgress(this.steps);
  },
  methods: {
    ...mapMutations("user", ["setUser"]),
    ...mapMutations("app", ["setReloadProgress"]),
    confirmDeleteStep(step) {
      this.confirmDialog = true
      this.selectedStep = step
    },
    closeConfirmDialog(confirmed) {
      this.confirmDialog = false
      if (confirmed) {
        localStorage.setItem('stepSRM', true);
        delete this.steps.step5;
        this.checkedProgress(this.steps);
      }
      this.selectedStep = null
    },
    checkedProgress(checkSteps) {
      let countProgress = 0;
      const stepsDone = [];
      const stepPercent = 100 / Object.keys(checkSteps).length
      let lengthSteps = Object.keys(checkSteps).length;
      for(let step in checkSteps) {
        if(checkSteps[step].done) {
          stepsDone.push(step);
          countProgress += stepPercent;
          lengthSteps --;
        }
      }
      this.nextStepRoute(stepsDone);
      this.stepsLeft = lengthSteps;
      this.progress = countProgress;
    },
    nextStepRoute(arrayStepDone) {
      const lastStepDone = arrayStepDone[arrayStepDone.length - 1];
      let lastStep = localStorage.getItem("last-step");
      if(lastStep && lastStep < arrayStepDone.length ) {
        localStorage.setItem("last-step", arrayStepDone.length);
      }
      if(!lastStep) {
        localStorage.setItem("last-step", arrayStepDone.length);
        lastStep = arrayStepDone.length;
      }
      switch(lastStepDone) {
        case "step2":
          if(lastStep < 2) {
            this.$router.push("/whitelabel/create");
          }
          break;
        case "step3":
          if(lastStep < 3) {
            this.$router.push("/gateways");
          }
          break;
        case "step4":
          if(lastStep < 4) {
            if(Object.prototype.hasOwnProperty.call(this.steps, "step5")) {
              this.$router.push("/modules/configuration");
            } else {
              this.$router.push("/users");
            }
          }
          break;
        default:
          return
      }
    },
    closePlatformProgress() {
      this.loading = true;
        let form = {
          onboarding: true,
        };

        this.$apollo
          .mutate({
            mutation: UPDATE_USER_MUTATION,
            variables: form
          })
          .then(({loading}) => {
            if (!loading) {
              this.reloadUser();
              localStorage.removeItem('stepSRM');
            }
          })
          .catch(error => {
            this.loading = false;
            this.errorHandler(error);
          });
    },
    reloadUser() {
      this.loading = true;
      this.$apollo
        .query({
          fetchPolicy: "network-only",
          query: ME_QUERY,
        })
        .then(({loading, data}) => {
          if (!loading) {
            this.setUser(data.me);
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
  apollo: {
    whiteLabelGateways: {
      fetchPolicy: "network-only",
      query() {
        return WL_GATEWAYS;
      },
      variables() {
        {
          return {
            whitelabel_uuid: this.getWhiteLabel.uuid
          }
        }
      },
      skip() {
        return !this.getWhiteLabel;
      },
    },
    whiteLabelUsers: {
      fetchPolicy: 'network-only',
      query() {
        return WHITELABEL_USERS_QUERY;
      },
      variables() {
        {
          return {
            whitelabel_uuid: this.getWhiteLabel.uuid
          }
        }
      },
      skip() {
        return !this.getWhiteLabel;
      },
    },
    whiteLabelModules: {
      fetchPolicy: 'network-only',
      query() {
        return NODULES_QUERY;
      },
      variables() {
        return {
          whitelabel_uuid: this.getWhiteLabel.uuid,
        };
      },
      skip() {
        return !this.getWhiteLabel || localStorage.getItem('stepSRM');
      },
    },
    providerPricings: {
      fetchPolicy: "no-cache",
      query() {
        return PRICINGS_QUERY;
      },
      variables() {
        return {
          provider_uuid: this.$ls.get('config').uuid,
        };
      },
      result({ loading, data }) {
        this.loading = loading;
        if (data.providerPricings && data.providerPricings.length === 0) {
          delete this.steps.step2;
          this.$router.push("/whitelabel/create");
        }
      },
      skip() {
        return !this.$ls.get('config').uuid;
      },
    }
  },
}
</script>
<style lang="scss">
@use "@/styles/material-dashboard/_colors.scss" as colors;
@use "@/styles/material-dashboard/_variables.scss" as variables;

.platform-progress {
  position: fixed;
  bottom: 12px;
  right: 12px;
  max-width: 400px;
  min-height: 50px;
  @media (max-width: 768px) {
    max-width: 350px;
  }
  @media (max-width: 425px) {
    max-width: none;
    width: 94%;
  }
  .v-expansion-panels {
    border-radius: 10px;
    &.theme--light {
      .v-expansion-panel {
        .v-expansion-panel-header {
          &.v-expansion-panel-header--active {
            color: rgba(0, 0, 0, 0.87);
            .v-icon {
              color: rgba(0, 0, 0, 0.87) !important;
            }
          }
        }
      }
    }
    &.theme--dark {
      background-color: variables.$body-color-dark;
      .v-expansion-panel {
        background-color: variables.$body-color-dark;
        .v-expansion-panel-header {
          &.v-expansion-panel-header--active {
            color: #FFFFFF !important;
            .v-icon {
              color: #FFFFFF !important;
            }
          }

        }
      }
    }
    .v-expansion-panel, .v-expansion-panel.v-expansion-panel--active {
      border-radius: 10px !important;
      .v-expansion-panel-header {
        background-color: var(--primary-color, variables.$brand-primary);
        font-size: variables.$font-size-17;
        font-weight: 500;
        line-height: 16px;
        color: #FFFFFF;
        border-radius: 10px;
        padding: 16px;
        .steps-count {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background-color: #FFFFFF;
          color: var(--primary-color, variables.$brand-primary);
          font-weight: 600;
          margin: 0 16px;
          @media (max-width: 768px) {
            margin-right: 8px;
          }
        }
        .v-expansion-panel-header__icon {
          .v-icon {
            color: #FFFFFF;
          }
        }
        &.v-expansion-panel-header--active {
          background-color: unset;
          .steps-count {
            background-color: var(--primary-color, variables.$brand-primary);
            color: #FFFFFF;
          }
        }
      }
      .v-expansion-panel-content {
        max-height: 70vh;
        overflow-y: auto;
        .v-expansion-panel-content__wrap {
          padding: 16px;
          .v-progress-linear {
            border-radius: 25px;
            &.theme--light {
              .v-progress-linear__background {
                background-color: variables.$body-color-light !important;
                border-color: variables.$body-color-light !important;
                opacity: 1 !important;
              }
            }
            .v-progress-linear__content {
              padding-left: 15px;
              justify-content: left;
              color: #FFFFFF;
              font-size: variables.$font-size-13;
            }
          }
          .info-card {
            padding: 0;
            &.theme--dark {
              background-color: variables.$body-color-dark;
              &.v-sheet {
                background-color: variables.$body-color-dark !important;
              }
            }
            .circle-outline {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 40px;
              height: 40px;
              border-radius: 50%;
              border: 3px solid variables.$brand-primary;
              &.check-circle {
                background-color: variables.$brand-primary;
              }
              .v-icon {
                color: #FFFFFF;
              }
            }
            .v-list-item {
              .v-list-item__title {
                font-weight: 600;
              }
              .v-list-item__subtitle {
                text-overflow: unset;
                white-space: normal;
              }
            }
          }
          .success-steps {
            &.theme--dark {
              background-color: variables.$body-color-dark;
              &.v-sheet {
                background-color: variables.$body-color-dark !important;
              }
            }
            .btn-close {
              width: 100%;
            }
          }

        }
      }

    }
  }
}
</style>
